import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Zustandsnoten from "@/components/Zustandsnoten";
import { SERVICES, IMAGES, CONTACT } from "@/lib/data";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/leistungen" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid="service-detail-page" className="bg-[color:var(--cream)]">
      <section className="pt-36 lg:pt-44 pb-16 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <Link to="/leistungen" className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase link-underline mb-8" data-testid="back-to-services">
            <ArrowLeft className="h-3 w-3" /> Alle Leistungen
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-6">
              <div className="font-serif text-8xl text-[color:var(--burgundy)]/40">{service.number}</div>
              <div className="eyebrow mt-4">{service.tagline}</div>
              <h1 className="display-xl mt-4 text-5xl md:text-6xl lg:text-7xl leading-[0.95]">{service.title}</h1>
              <p className="mt-8 text-[color:var(--ink-2)] text-lg leading-relaxed max-w-xl">{service.description}</p>
            </div>
            <div className="lg:col-span-6">
              <div className="relative overflow-hidden aspect-[4/5] bg-black">
                <img src={IMAGES[service.image]} alt={service.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 lg:px-10 bg-[color:var(--cream-2)]">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <div className="eyebrow mb-4">Ideal für</div>
            <h2 className="font-serif text-3xl leading-[1.15]">{service.ideal}</h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
            <div className="eyebrow mb-4">Leistungsumfang</div>
            <ul className="space-y-4">
              {service.includes.map((it) => (
                <li key={it} className="flex items-start gap-3 border-b border-black/10 pb-4 text-[color:var(--ink)]">
                  <CheckCircle2 className="h-5 w-5 mt-[2px] text-[color:var(--burgundy)] shrink-0" /> <span className="text-[15px]">{it}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-t border-black/15 pt-10">
          <div>
            <div className="font-serif text-3xl md:text-4xl">Bereit für Ihr Gutachten?</div>
            <p className="mt-2 text-[color:var(--ink-2)]">Rufen Sie uns unter <a href={`tel:${CONTACT.phoneRaw}`} className="link-underline text-[color:var(--ink)]">{CONTACT.phone}</a> oder senden Sie eine Anfrage.</p>
          </div>
          <Link to="/kontakt" className="btn-primary" data-testid="service-detail-cta">Anfrage senden <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {(service.slug === "ausfuehrliche-fahrzeugbewertung" || service.slug === "kurzbewertung") && <Zustandsnoten />}

      <section className="py-24 px-6 lg:px-10 bg-[color:var(--cream-2)]">
        <div className="mx-auto max-w-[1400px]">
          <div className="eyebrow mb-6">— Weitere Leistungen</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {others.map((s) => (
              <Link key={s.slug} to={`/leistungen/${s.slug}`} className="group block">
                <div className="relative overflow-hidden aspect-[16/9] bg-black">
                  <img src={IMAGES[s.image]} alt={s.title} className="w-full h-full object-cover opacity-90 group-hover:scale-105 group-hover:opacity-100 transition-all duration-700" />
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-between text-[color:var(--cream)]">
                    <div className="text-xs tracking-[0.24em]">{s.number}</div>
                    <div>
                      <div className="font-serif text-3xl">{s.title}</div>
                      <div className="text-xs opacity-70 mt-1">{s.tagline}</div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
